import { useToast } from 'vue-toastification'
import { createRouter, createWebHistory, RouteLocation, RouteRecordRaw } from 'vue-router'

import { useCommerceStore } from '@/stores/commerce'
import { useMainStore } from '@/stores/main'
import { usePackageBuilderStore } from '@/stores/packageBuilder'

import { substitute } from '@/composables/contentful'
import { commerceStepAtPath } from '@/lib/commerceSteps'
import { featureFlags } from '@/lib/featureFlags'
import { isPortfolioTrackerApplicable } from '@/lib/portfolioTrackerDisplay'
import { PRIMARY_MARKET_LANDING_ROUTE, routeName } from '@/router/routeName'
import { appErrorService } from '@/services/AppErrorService'

import { DisclaimerVariant } from '@/components/Shared/AppDisclaimers.vue'
import AppBareLayout from '@/layout/AppBareLayout.vue'
import AppLayout from '@/layout/AppLayout.vue'

import { CadyMarket } from '@/types/CadyMarket'
import { CartFlyoutEnhancement } from '@/types/CartFlyoutEnhancement'
import { PACKAGE_BUILDER_STEP_ID } from '@/types/CommerceStep'

import { feedbackContent } from '@/content/feedback'
import { pagesContent } from '@/content/pages'

/**
 * The enhancement sandbox (SUN-292), which is a development and demo surface and never a production
 * one. Empty in a production build, so the route never enters the table.
 *
 * Vite replaces `MODE` with a literal at build time, so the ternary folds and Rollup drops the
 * branch holding the dynamic import — the harness chunk is not emitted at all. Unshipped, not merely
 * unlinked. Keep the `import()` inside this constant for that reason: hoisting it to a module-level
 * import would ship the harness in every build regardless of the gate.
 *
 * `MODE !== 'production'` rather than `import.meta.env.PROD`, matching `AppErrorService`. `PROD` is
 * true for *any* build, demo included, and demo is exactly where the business reviews the sequence —
 * gating on it would take the sandbox down where it is most used.
 */
const debugCartFlyoutEnhancementRoutes: RouteRecordRaw[] =
  import.meta.env.MODE !== 'production'
    ? [
        {
          path: 'cart-flyout-enhancement',
          name: routeName.DEBUG_CART_FLYOUT_ENHANCEMENT,
          component: () => import('@/views/Debug/CartFlyoutEnhancementView.vue'),
          // `cartFlyoutEnhancement` is load-bearing, not decorative: the harness renders the enhanced
          // flyout, and `isEnhancedFlyoutActive` is false on any route that hasn't opted into the
          // allow-list. Opting in here is the gate override the harness needs — a positional clause
          // on a positional gate, so it needs no new mechanism (see the gate's ADR).
          //
          // `Always` rather than `WhenEnabled`, because the sandbox has no previous behaviour for the
          // kill switch to restore — it renders the new sequence and nothing else. Sharing that switch
          // made the sandbox go dark exactly when it was most needed, through phase 1, while the
          // switch was off and live adds still raised the legacy toasts. Phase 2 has since turned the
          // switch on, but the separation stands on the sandbox having no "before" to fall back to,
          // not on which way the switch currently points.
          meta: {
            title: 'Cart Flyout Enhancement',
            cartFlyoutEnhancement: CartFlyoutEnhancement.Always
          }
        }
      ]
    : []

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/',
      component: AppLayout,
      children: [
        {
          path: '/',
          name: routeName.HOME,
          component: () => import('@/views/HomeView.vue'),
          meta: { title: pagesContent.homePageTitle, showBookingUi: true }
        },
        {
          path: '/cap-and-gown',
          name: routeName.MARKET_LANDING_CAP_GOWN,
          component: () => import('@/views/MarketLandingView.vue'),
          props: { cadyMarket: CadyMarket.CapGown },
          meta: {
            title: pagesContent.capGownPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.CapGown
          }
        },
        {
          path: '/graduation',
          name: routeName.MARKET_LANDING_GRADUATION,
          component: () => import('@/views/MarketLandingView.vue'),
          props: { cadyMarket: CadyMarket.Graduation },
          meta: {
            title: pagesContent.graduationPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.Graduation
          }
        },
        {
          path: '/athletic',
          name: routeName.MARKET_LANDING_ATHLETIC,
          component: () => import('@/views/MarketLandingView.vue'),
          props: { cadyMarket: CadyMarket.Athletic },
          meta: {
            title: pagesContent.athleticPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.Athletic
          }
        },
        {
          path: '/underclass',
          name: routeName.MARKET_LANDING_UNDERCLASS,
          component: () => import('@/views/MarketLandingView.vue'),
          props: { cadyMarket: CadyMarket.Underclass },
          meta: {
            title: pagesContent.underclassPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.Underclass
          }
        },
        {
          path: '/senior',
          name: routeName.MARKET_LANDING_SENIOR,
          component: () => import('@/views/MarketLandingView.vue'),
          props: { cadyMarket: CadyMarket.Senior },
          meta: {
            title: pagesContent.seniorsPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.Senior
          }
        },
        {
          // Senior Preorder marketing-entry page (reached only via email/SMS
          // `?s=TOKEN` links; not linked from the dashboard or in-app nav).
          path: '/senior/preorder',
          name: routeName.MARKET_LANDING_SENIOR_PREORDER,
          component: () => import('@/views/Senior/PreorderView.vue'),
          props: { cadyMarket: CadyMarket.Senior },
          meta: {
            title: pagesContent.seniorPreorderPageTitle,
            whiteContentBackground: true,
            cadyMarket: CadyMarket.Senior
          }
        },
        {
          path: '/catalog',
          name: routeName.CATALOG_INDEX,
          redirect: {
            name: routeName.CATALOG_FLOW_STEP,
            params: { flowStep: 1 }
          }
        },
        {
          path: '/catalog/flow/:flowStep',
          name: routeName.CATALOG_FLOW_STEP,
          // Enhanced cart flyout (SUN-292). The pose/background-selection step shares this route, and
          // is excluded inside `useEnhancedFlyout` — a route-level flag cannot express it.
          meta: { cartFlyoutEnhancement: CartFlyoutEnhancement.WhenEnabled },
          component: () => import('@/views/Catalog/IndexFlowView.vue')
        },
        {
          path: '/catalog/package-builder',
          name: routeName.PACKAGE_BUILDER,
          component: () => import('@/views/Catalog/PackageBuilderStepView.vue')
        },
        {
          path: '/cart/configure',
          name: routeName.CART_CONFIGURE,
          component: () => import('@/views/Cart/ConfigureView.vue')
        },
        {
          path: '/yearbook',
          name: routeName.YEARBOOK,
          component: () => import('@/views/Senior/YearbookView.vue'),
          meta: { title: pagesContent.yearbookPageTitle },
          beforeEnter: (_to, _from, next) => {
            // Defensive guard for direct URL navigation; the step is already
            // gone from the nav when the flag is off (via the null
            // `yearbookContext` getter cascade), so this only catches manual
            // address-bar entry.
            if (!featureFlags.yearbookSelection) {
              next({ name: routeName.HOME })
              return
            }
            next()
          }
        },
        {
          path: '/retouching',
          name: routeName.RETOUCHING,
          // Opted into the enhanced flyout so a reward unlocked by adding retouching gets the same
          // celebration it would on the catalog step. This reverses the day-1 deny listed in the
          // SUN-292 hand-off (§2.1), which the business flagged as revisitable; the reward-claim
          // modal remains the celebration where the reward needs configuring, via the per-mutation
          // suppression `RetouchingBundle` passes.
          meta: {
            blackContentBackground: true,
            disclaimerVariant: DisclaimerVariant.Dark,
            cartFlyoutEnhancement: CartFlyoutEnhancement.WhenEnabled
          },
          component: () => import('@/views/Catalog/RetouchingView.vue')
        },
        {
          path: '/cart/checkout',
          name: routeName.CART_CHECKOUT,
          // No cart-mutating YMAL Quick Adds on the final review/pay step (see `useQuickAdds`).
          meta: { suppressCartFlyoutQuickAdds: true },
          component: () => import('@/views/Cart/CheckoutView.vue')
        },
        {
          path: '/cart/checkout/order-complete-stripe',
          name: routeName.CART_ORDER_COMPLETE_STRIPE,
          meta: { skipPaymentHoldModal: true, ignoresOtherTabs: true },
          component: () => import('@/views/Cart/OrderCompleteStripeView.vue')
        },
        {
          path: '/receipt/:orderId/:cartSecurityKey',
          name: routeName.RECEIPT,
          meta: { skipPaymentHoldModal: true, ignoresOtherTabs: true },
          component: () => import('@/views/ReceiptView.vue')
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          component: () => import('@/views/NotFoundView.vue')
        }
      ]
    },
    {
      path: '/order-insert',
      name: 'order-insert-parent',
      component: AppBareLayout,
      children: [
        {
          path: '/order-insert/:orderId/:vendorId/:cartSecurityKey',
          name: routeName.ORDER_INSERT,
          component: () => import('@/views/OrderInsertView.vue')
        }
      ]
    },
    {
      path: '/debug',
      name: 'debug-parent',
      component: AppBareLayout,
      children: [
        {
          path: 'reset-data',
          name: routeName.DEBUG_RESET_DATA,
          component: () => import('@/views/Debug/ResetDataView.vue'),
          meta: { title: 'Reset Data', skipPaymentHoldModal: true }
        },
        ...debugCartFlyoutEnhancementRoutes
      ]
    },
    {
      path: '/customer-service',
      name: 'customer-service-parent',
      component: AppLayout,
      meta: { isCustomerServiceView: true },
      children: [
        {
          path: 'order/:cartId/:cartSecurityKey',
          name: routeName.CUSTOMER_SERVICE_ORDER,
          component: () => import('@/views/CustomerService/OrderView.vue')
        }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (to.meta.title) {
    document.title = substitute(pagesContent.documentTitleTemplate, {
      SiteName: pagesContent.siteName,
      PageTitle: String(to.meta.title)
    })
  }

  const mainStore = useMainStore()

  mainStore.showCartSidebar = false

  next()
})

// Package Builder backstop. While any SheetSelection bundle in the cart is added
// but not yet *deliberately* configured (it carries only the backend default seed),
// block navigation to any commerce step ordered after the Package Builder step —
// i.e. top-bar jumps and direct URLs — and send the customer back to finish it. The
// step's own Continue is already gated; this catches the bypasses. Fails open if the
// builder step isn't resolvable (e.g. missing flow-step content) so we never trap.
router.beforeEach((to, from, next) => {
  const packageBuilderStore = usePackageBuilderStore()

  if (!packageBuilderStore.hasUnconfiguredBundles || to.name === routeName.PACKAGE_BUILDER) {
    next()
    return
  }

  const steps = useCommerceStore().commerceAndCatalogSteps
  const builderStep = steps.find((step) => step.id === PACKAGE_BUILDER_STEP_ID)
  const targetStep = commerceStepAtPath(steps, to.path)

  if (
    builderStep &&
    builderStep.number !== null &&
    targetStep &&
    targetStep.number !== null &&
    targetStep.number > builderStep.number
  ) {
    // `error` variant (not `warning`) — its palette contrasts legibly; the
    // warning variant's colors wash out against the page.
    useToast().error(feedbackContent.finishPackageToContinue)
    next({ name: routeName.PACKAGE_BUILDER })
    return
  }

  next()
})

// Terms & Conditions acceptance gate. A subject with an active session must
// accept T&C before reaching any commerce surface. Acceptance is re-read from
// the accepted-tokens cookie on every genuine session entry (see `useDataLoader`),
// so an unaccepted session that deep-links straight to e.g. the catalog or cart is
// forwarded to the current market's landing page, where the blocking sheet is shown. The landings
// themselves, home, and non-commerce surfaces (order insert, debug, customer
// service) are exempt — both to avoid a redirect loop and to leave support
// flows unaffected.
router.beforeEach((to, from, next) => {
  const { cadySessionToken, cadyMarket, termsAccepted, termsAcceptanceGateExempt } = useMainStore()

  if (
    featureFlags.termsAcceptanceGate &&
    cadySessionToken &&
    cadyMarket &&
    !termsAccepted &&
    !termsAcceptanceGateExempt &&
    !routeIsExemptFromTermsAcceptance(to)
  ) {
    next({ name: PRIMARY_MARKET_LANDING_ROUTE[cadyMarket] })
    return
  }

  next()
})

// Auto-open the Portfolio Tracker modal on the first landing of the Package
// Listing Page (`CATALOG_FLOW_STEP` with `flowStep=1`) per session, for
// Senior subjects whose Portfolio overlay is applicable (Portfolio prooftype +
// before Deadline). Subsequent navigations to PLP within the same session do
// not re-open. Other catalog flow steps (Frames & Prints, Package Builder)
// and the Yearbook / Configure surfaces are click-only.
router.afterEach((to) => {
  if (to.name !== routeName.CATALOG_FLOW_STEP) return
  if (String(to.params.flowStep) !== '1') return

  const main = useMainStore()
  if (main.portfolioTrackerAutoOpenedThisSession) return
  if (!isPortfolioTrackerApplicable(main.portfolioTrackerDetail)) return

  main.openPortfolioTrackerModal()
  main.markPortfolioTrackerAutoOpened()
})

router.onError((err, to) => {
  // Error message strings related to dynamic imports failing after publish
  const dynamicImportErrors = [
    'failed to fetch dynamically imported module',
    'unable to preload css',
    'importing a module script failed',
    'error loading dynamically imported module'
  ]

  // If the error message contains any of the dynamic import errors
  if (dynamicImportErrors.some((e) => err.message.toLowerCase().includes(e.toLowerCase()))) {
    // Do a full browser refresh of the page the user is navigating to ensure the latest code is used
    window.location.href = to.fullPath
    return
  }

  const mainStore = useMainStore()
  appErrorService.reportVueRouterError(err, {
    cadyMarket: mainStore.cadyMarket,
    cadySessionToken: mainStore.cadySessionToken
  })
})

function routeIsExemptFromTermsAcceptance(to: RouteLocation): boolean {
  return (
    // Any market entry surface — `meta.cadyMarket` is set (the five landings plus
    // `senior-preorder`). Exempt so an unaccepted session isn't bounced away from
    // the very page that shows the T&C sheet / handles terms downstream.
    to.meta.cadyMarket != null ||
    to.name === routeName.HOME ||
    to.name === routeName.ORDER_INSERT ||
    to.name === routeName.DEBUG_RESET_DATA ||
    to.name === routeName.DEBUG_CART_FLYOUT_ENHANCEMENT ||
    to.name === routeName.CUSTOMER_SERVICE_ORDER ||
    to.name === 'not-found' ||
    to.meta.isCustomerServiceView === true
  )
}

export default router
